import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["actions", "modalActionTargetEntity", "managementDataType", "modalRelevantEntities", "openModalName"];
/* hs-eslint ignored failing-rules */
/* eslint-disable no-prototype-builtins */

import Raven from 'raven-js';
import { OrderedMap } from 'immutable';
import ModalDataTypes from 'ContentComponents/constants/ModalDataTypes';
import UIDialogCloseButton from 'UIComponents/dialog/UIDialogCloseButton';
import UIDialogHeader from 'UIComponents/dialog/UIDialogHeader';
import H2 from 'UIComponents/elements/headings/H2';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { ModalPanel } from 'foundations-components/transitional/components/ModalPanel';
import { Modal } from 'foundations-components/transitional/components/Modal';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
// Given maps of modal components, returns a function that renders the open modal
// with the passed props and relevant data needed
export default (({
  // Components that are the modal content, as the shell and header
  // will be rendered here while loading the content async
  modalMap = {},
  // Same but for modal content that needs a panel shell
  panelMap = {},
  // These are for modals/panels that cannot have their content
  // extracted out to split the shell and content
  noShellMap = {},
  // If modalDataType cannot be determined on Modal creation/setup, you can
  // override it by passing the same prop to the modal at render time
  modalDataType,
  // Enable this only if you have one modal renderer component in the app at a time
  // Otherwise, if 2 renderers hold different modals, it will log sentries for each other
  logSentryForMissingModal = false
}) => {
  const getModalComponent = openModalName => panelMap.hasOwnProperty(openModalName) ? ModalPanel : Modal;
  const renderHeaderText = (modalData, props) => {
    const {
      modalActionTargetEntity,
      modalRelevantEntities = OrderedMap(),
      translatedContentKey = 'translatedContent'
    } = props;
    const {
      headerLangKey,
      mlangGroupHeaderLangKey,
      headerTextRenderer
    } = modalData;
    const actionTargetCount = modalActionTargetEntity ? 1 : modalRelevantEntities.size;
    const actionTargetTranslationsCount = modalRelevantEntities.reduce((acc, page) => {
      if (page.has(translatedContentKey)) {
        return acc + page.get(translatedContentKey).size;
      }
      return acc;
    }, 0);
    const headerMessageOptions = {
      count: actionTargetCount,
      translationsCount: actionTargetTranslationsCount
    };
    if (headerLangKey) {
      if (mlangGroupHeaderLangKey && actionTargetCount === 1 && modalRelevantEntities.first().get(translatedContentKey).size > 0) {
        return /*#__PURE__*/_jsx(FormattedMessage, {
          message: mlangGroupHeaderLangKey,
          options: headerMessageOptions
        });
      }
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: headerLangKey,
        options: headerMessageOptions
      });
    } else if (headerTextRenderer && typeof headerTextRenderer === 'function') {
      return headerTextRenderer(Object.assign({}, props, {
        modalRelevantEntities
      }, headerMessageOptions));
    }
    return null;
  };
  const renderHeader = (modalData, props) => {
    const {
      actions
    } = props;
    const {
      headerLangKey,
      headerTextRenderer,
      shouldRenderModalCloseButton = true
    } = modalData;
    if (!headerLangKey && !headerTextRenderer) {
      return null;
    }
    return /*#__PURE__*/_jsxs(UIDialogHeader, {
      children: [shouldRenderModalCloseButton && /*#__PURE__*/_jsx(UIDialogCloseButton, {
        onClick: actions.closeModal
      }), /*#__PURE__*/_jsx(H2, {
        children: renderHeaderText(modalData, props)
      })]
    });
  };
  return props => {
    const {
        actions,
        modalActionTargetEntity,
        managementDataType,
        modalRelevantEntities,
        openModalName
      } = props,
      otherProps = _objectWithoutPropertiesLoose(props, _excluded);
    if (!openModalName) {
      return null;
    }
    const modalContentProps = Object.assign({}, actions, {
      managementDataType,
      modalDataType: modalDataType || props.modalDataType || ModalDataTypes.PAGES,
      modalActionTargetEntity,
      modalRelevantEntities
    }, otherProps);
    const ModalWithoutShell = noShellMap[openModalName];
    if (ModalWithoutShell) {
      return /*#__PURE__*/_jsx(ModalWithoutShell, Object.assign({}, modalContentProps));
    }
    const modalData = modalMap[openModalName] || panelMap[openModalName];
    if (!modalData) {
      if (logSentryForMissingModal) {
        Raven.captureMessage(`Modal with name "${openModalName}" has no entry in Modal Map`);
      }
      return null;
    }
    const {
      component: ModalContent,
      modalProps
    } = modalData;
    if (!ModalContent) {
      Raven.captureMessage(`Modal with name "${openModalName}" has no component`);
      return null;
    }
    const modalFinalProps = Object.assign({}, modalProps);
    const ModalComponent = getModalComponent(openModalName);
    return /*#__PURE__*/_jsxs(ModalComponent, Object.assign({}, modalFinalProps, {
      children: [renderHeader(modalData, props), /*#__PURE__*/_jsx(ModalContent, Object.assign({}, modalContentProps))]
    }));
  };
});